import React, { useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  FaTachometerAlt,
  FaBoxOpen,
  FaFileInvoice,
  FaFileAlt,
  FaTruck,
  FaList,
  FaArrowDown,
  FaTags,
  FaExclamationTriangle,
  FaArrowUp,
  FaClipboardList,
  FaFileInvoiceDollar,
  FaUsers,
  FaBoxes,
  FaShoppingCart,
  FaUserCheck,
  FaUserCog,
  FaCog,
  FaPercent,
  FaClipboardCheck,
  FaShieldAlt,
  FaPlusCircle,
  FaListAlt,
  FaMoneyBillWave,
  FaUserPlus,
  FaUserTag,
  FaCalendarCheck,
  FaBuilding,
  FaPhoneAlt,
  FaUserCircle,
  FaFileContract,
  FaReceipt,
  FaChartLine,
  FaMedkit,
  FaCapsules,
  FaClinicMedical,
  FaPills,
  FaFileMedical,
  FaPrescriptionBottleAlt,
  FaCalendarTimes,
  FaHdd,
  FaTimes,
} from "react-icons/fa";

// ── Responsive hook ──
const useWindowWidth = () => {
  const [width, setWidth] = React.useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
};

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const HEADER_HEIGHT = "65px";
  const width = useWindowWidth();
  const isMobile = width <= 768;

  // On mobile: sidebar is either fully open (overlay) or fully hidden
  // On desktop: sidebar is either expanded (300px) or collapsed (60px)
  const sidebarWidth = isMobile
    ? isOpen ? "260px" : "0px"
    : isOpen ? "300px" : "60px";

  const sidebarVisible = isMobile ? isOpen : true;

  // Close sidebar on mobile when a nav link is clicked
  const handleNavClick = () => {
    if (isMobile && toggleSidebar) toggleSidebar();
  };

  // Get user and permissions from localStorage
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  console.log(user);

  const userPermissions = user?.permissions || [];
  const userType = user?.user_type || "";

  const hasPermission = (submodule_id) => {
    const isAdmin = userType?.toLowerCase() === "admin";
    if (isAdmin) return true;
    if (!Array.isArray(userPermissions)) return false;
    const perm = userPermissions.find((p) => p.submodule_id === submodule_id);
    return perm ? perm.view === true : false;
  };

  const isSectionVisible = (submodule_ids) => {
    const isAdmin = userType?.toLowerCase() === "admin";
    if (isAdmin) return true;
    return submodule_ids.some((id) => hasPermission(id));
  };

  // Whether text labels should show
  const showText = isMobile ? true : isOpen;

  const styles = {
    // Backdrop overlay (mobile only)
    backdrop: {
      display: isMobile && isOpen ? "block" : "none",
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.55)",
      zIndex: 1500,
      top: HEADER_HEIGHT,
    },

    sidebar: {
      width: sidebarWidth,
      height: `calc(100vh - ${HEADER_HEIGHT})`,
      background: "linear-gradient(180deg, #111827, #0f172a)",
      color: "#fff",
      padding: sidebarVisible ? "20px 8px" : "0",
      position: "fixed",
      top: HEADER_HEIGHT,
      left: 0,
      transition: "width 0.3s ease, padding 0.3s ease",
      overflowY: "auto",
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
      boxShadow: isOpen ? "4px 0 20px rgba(0,0,0,0.4)" : "none",
      scrollbarWidth: "thin",
      scrollbarColor: "#10b981 #1f2937",
      zIndex: 1600,
      // Hide content when collapsed on mobile
      visibility: isMobile && !isOpen ? "hidden" : "visible",
    },

    logoSection: {
      marginBottom: "25px",
      fontSize: "16px",
      fontWeight: "600",
      letterSpacing: "1px",
      textAlign: "center",
      padding: "8px 4px",
      borderBottom: "1px solid rgba(16, 185, 129, 0.2)",
      whiteSpace: "nowrap",
      overflow: "hidden",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    },

    navContainer: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      flex: 1,
    },

    link: {
      display: "flex",
      alignItems: "center",
      justifyContent: showText ? "flex-start" : "center",
      gap: "12px",
      padding: "10px 12px",
      color: "#9ca3af",
      textDecoration: "none",
      borderRadius: "8px",
      transition: "all 0.2s ease",
      fontSize: "14px",
      fontWeight: "500",
      whiteSpace: "nowrap",
      minHeight: "40px",
    },

    activeLink: {
      background: "rgba(16, 185, 129, 0.15)",
      color: "#10b981",
    },

    icon: {
      fontSize: "18px",
      minWidth: "20px",
      flexShrink: 0,
    },

    text: {
      display: showText ? "inline" : "none",
      opacity: showText ? 1 : 0,
      transition: "opacity 0.2s ease",
    },

    divider: {
      height: "1px",
      background: "rgba(255,255,255,0.1)",
      margin: "12px 0",
    },

    sectionTitle: {
      fontSize: "11px",
      fontWeight: "600",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      color: "#6b7280",
      padding: "8px 12px 4px",
      display: showText ? "block" : "none",
    },
  };

  const scrollbarStyles = `
    .sidebar::-webkit-scrollbar { width: 4px; }
    .sidebar::-webkit-scrollbar-track { background: #1f2937; }
    .sidebar::-webkit-scrollbar-thumb { background: #10b981; border-radius: 4px; }
    .sidebar::-webkit-scrollbar-thumb:hover { background: #10b981; }
  `;

  const getLinkStyle = ({ isActive }) =>
    isActive ? { ...styles.link, ...styles.activeLink } : styles.link;

  return (
    <>
      <style>{scrollbarStyles}</style>

      {/* Mobile backdrop — tap to close */}
      <div style={styles.backdrop} onClick={handleNavClick} />

      <div style={styles.sidebar} className="sidebar">
        {/* Logo / Brand */}
        <div style={styles.logoSection}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {showText ? "GANAPATHY MEDICALS" : "GM"}
          </span>
          {/* Close button shown inside sidebar on mobile */}
          {isMobile && isOpen && toggleSidebar && (
            <button
              onClick={toggleSidebar}
              style={{
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "#9ca3af",
                borderRadius: 6,
                cursor: "pointer",
                padding: "4px 8px",
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              <FaTimes />
            </button>
          )}
        </div>

        <div style={styles.navContainer}>
          {/* Main Navigation */}
          {isSectionVisible(["dashboard"]) && (
            <>
              <div style={styles.sectionTitle}>Main</div>
              {hasPermission("dashboard") && (
                <NavLink to="/dashboard" style={getLinkStyle} onClick={handleNavClick}>
                  <FaTachometerAlt style={styles.icon} />
                  <span style={styles.text}>Dashboard</span>
                </NavLink>
              )}
            </>
          )}

          {/* Inventory Management */}
          {isSectionVisible(["products", "category", "stock_in", "stock_out", "low_stock"]) && (
            <>
              <div style={styles.sectionTitle}>Inventory</div>
              {hasPermission("products") && (
                <NavLink to="/product" style={getLinkStyle} onClick={handleNavClick}>
                  <FaMedkit style={styles.icon} />
                  <span style={styles.text}>Medicines</span>
                </NavLink>
              )}
              {hasPermission("category") && (
                <NavLink to="/type" style={getLinkStyle} onClick={handleNavClick}>
                  <FaCapsules style={styles.icon} />
                  <span style={styles.text}>Category</span>
                </NavLink>
              )}
              {hasPermission("stock_in") && (
                <NavLink to="/itemlist" style={getLinkStyle} onClick={handleNavClick}>
                  <FaClinicMedical style={styles.icon} />
                  <span style={styles.text}>Stock In</span>
                </NavLink>
              )}
              {hasPermission("stock_out") && (
                <NavLink to="/stockout" style={getLinkStyle} onClick={handleNavClick}>
                  <FaPills style={styles.icon} />
                  <span style={styles.text}>Stock Out</span>
                </NavLink>
              )}
              {hasPermission("low_stock") && (
                <NavLink to="/lowstock" style={getLinkStyle} onClick={handleNavClick}>
                  <FaExclamationTriangle style={styles.icon} />
                  <span style={styles.text}>Low Stock</span>
                </NavLink>
              )}
              <NavLink to="/product" style={getLinkStyle} onClick={handleNavClick}>
                <FaCalendarTimes style={styles.icon} />
                <span style={styles.text}>Expiry Status</span>
              </NavLink>
            </>
          )}

          {/* Billing Section */}
          {isSectionVisible(["create_bill", "bill_reports", "sales_bills", "quotations", "discount"]) && (
            <>
              <div style={styles.sectionTitle}>Pharmacy POS</div>
              {hasPermission("create_bill") && (
                <NavLink to="/bill" style={getLinkStyle} onClick={handleNavClick}>
                  <FaFileMedical style={styles.icon} />
                  <span style={styles.text}>New Sale / Bill</span>
                </NavLink>
              )}
              {hasPermission("bill_reports") && (
                <NavLink to="/billreport" style={getLinkStyle} onClick={handleNavClick}>
                  <FaChartLine style={styles.icon} />
                  <span style={styles.text}>Bill Reports</span>
                </NavLink>
              )}
              {/* {hasPermission("sales_bills") && (
                <NavLink to="/employeebill" style={getLinkStyle} onClick={handleNavClick}>
                  <FaUserCircle style={styles.icon} />
                  <span style={styles.text}>SalesBill(emp)</span>
                </NavLink>
              )} */}
              {hasPermission("quotations") && (
                <NavLink to="/quotation" style={getLinkStyle} onClick={handleNavClick}>
                  <FaClipboardList style={styles.icon} />
                  <span style={styles.text}>Quotations</span>
                </NavLink>
              )}
            </>
          )}

          {/* Supplier Section */}
          {isSectionVisible(["add_supplier", "supplier_list", "payment_tracking", "employee", "user_type", "company"]) && (
            <>
              <div style={styles.sectionTitle}>Suppliers & HR</div>
              {hasPermission("add_supplier") && (
                <NavLink to="/supplier" style={getLinkStyle} onClick={handleNavClick}>
                  <FaPlusCircle style={styles.icon} />
                  <span style={styles.text}>Add Supplier</span>
                </NavLink>
              )}
              {hasPermission("supplier_list") && (
                <NavLink to="/supplierList" style={getLinkStyle} onClick={handleNavClick}>
                  <FaListAlt style={styles.icon} />
                  <span style={styles.text}>Supplier List</span>
                </NavLink>
              )}
              {hasPermission("payment_tracking") && (
                <NavLink to="/paymenttracking" style={getLinkStyle} onClick={handleNavClick}>
                  <FaMoneyBillWave style={styles.icon} />
                  <span style={styles.text}>Payment Tracking</span>
                </NavLink>
              )}
              {hasPermission("employee") && (
                <NavLink to="/employee" style={getLinkStyle} onClick={handleNavClick}>
                  <FaUserPlus style={styles.icon} />
                  <span style={styles.text}>Employee</span>
                </NavLink>
              )}
              {hasPermission("user_type") && (
                <NavLink to="/usertype" style={getLinkStyle} onClick={handleNavClick}>
                  <FaUserTag style={styles.icon} />
                  <span style={styles.text}>User Type</span>
                </NavLink>
              )}
              {hasPermission("company") && (
                <NavLink to="/company" style={getLinkStyle} onClick={handleNavClick}>
                  <FaBuilding style={styles.icon} />
                  <span style={styles.text}>Company</span>
                </NavLink>
              )}
              {hasPermission("storage") && (
                <NavLink to="/storage" style={getLinkStyle} onClick={handleNavClick}>
                  <FaHdd style={styles.icon} />
                  <span style={styles.text}>Storage</span>
                </NavLink>
              )}
            </>
          )}

          {/* CRM Section */}
          {isSectionVisible(["enquiries", "customer_details", "usersettings"]) && (
            <>
              <div style={styles.sectionTitle}>CRM</div>
              {hasPermission("enquiries") && (
                <NavLink to="/enquiry" style={getLinkStyle} onClick={handleNavClick}>
                  <FaPhoneAlt style={styles.icon} />
                  <span style={styles.text}>Enquiries</span>
                </NavLink>
              )}
              {hasPermission("customer_details") && (
                <NavLink to="/customer" style={getLinkStyle} onClick={handleNavClick}>
                  <FaUsers style={styles.icon} />
                  <span style={styles.text}>Customer Details</span>
                </NavLink>
              )}
              {hasPermission("usersettings") && (
                <NavLink to="/usersettings" style={getLinkStyle} onClick={handleNavClick}>
                  <FaUserCog style={styles.icon} />
                  <span style={styles.text}>User Settings</span>
                </NavLink>
              )}
            </>
          )}
        </div>

        {showText && (
          <div style={{
            fontSize: "10px",
            color: "#6b7280",
            textAlign: "center",
            padding: "16px 0 8px",
            borderTop: "1px solid rgba(255,255,255,0.1)",
            marginTop: "auto",
          }}>
            v1.0.0
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;